import type { Event } from '@sentry/react';

/** Whether a Sentry event is one `reportSessionEvent` sent. */
export const isSessionEvent = (event: Event): boolean => !!event.tags?.session_reason;

const originOf = (url: string | undefined) => {
  if (!url) {
    return undefined;
  }
  try {
    return new URL(url).origin;
  } catch (error) {
    return undefined;
  }
};

/**
 * Cut a session event down to its message and coarse tags.
 *
 * Sentry attaches the breadcrumb trail (Core request URLs carry auth tokens and
 * patient ids), the page URL and transaction name (patient routes) and the
 * user. A session event needs none of them; only the page's origin is kept.
 * Every other event is returned untouched.
 */
export const scrubSessionEvent = <T extends Event>(event: T): T => {
  if (!isSessionEvent(event)) {
    return event;
  }

  const scrubbed: T = { ...event };
  delete scrubbed.breadcrumbs;
  delete scrubbed.request;
  delete scrubbed.user;
  delete scrubbed.extra;
  delete scrubbed.transaction;

  const origin = originOf(event.request?.url);
  if (origin) {
    scrubbed.request = { url: origin };
  }
  return scrubbed;
};
